import {
  AlignCenterHorizontalSimpleIcon,
  AlignCenterVerticalSimpleIcon,
  ArrowLineDownIcon,
  ArrowLineUpIcon,
  ArrowsVerticalIcon,
  CaretDownIcon,
  CircleIcon,
  DropHalfBottomIcon,
  MinusIcon,
  PlusIcon,
  RectangleIcon,
  StarIcon,
  TextAlignCenterIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
  TextBolderIcon,
  TextItalicIcon,
  TextUnderlineIcon,
  TriangleIcon,
  XIcon,
} from '@phosphor-icons/react';
import React, { useState } from 'react';

import { computeContrastPadding } from './KonvaTextNode';
import { ZOrderButtons } from './ZOrderButtons';
import { resolveFillColor, useTextEditorStore } from './useTextEditorStore';

import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { OVERLAY_FONT_FAMILIES, loadGoogleFont } from '@/lib/fonts/load-google-font';
import { cn } from '@/lib/utils';

// Single source of truth — the loader keeps the same list (see load-google-font.ts).
const FONTS = OVERLAY_FONT_FAMILIES;
const COLORS = ['#ffffff', '#000000', '#ef4444', '#3b82f6', '#10b981', '#f59e0b'];

const FONT_WEIGHTS = [
  { label: 'Thin', value: 100 },
  { label: 'Light', value: 300 },
  { label: 'Regular', value: 400 },
  { label: 'Medium', value: 500 },
  { label: 'SemiBold', value: 600 },
  { label: 'Bold', value: 700 },
  { label: 'ExtraBold', value: 800 },
  { label: 'Black', value: 900 },
];

const CONTRAST_MODE_CONFIG = {
  'drop-shadow': {
    sliderLabel: 'Intensity',
    defaultColor: '#000000',
    colorOptions: [
      { color: '#000000', label: 'Black' },
      { color: '#ffffff', label: 'White' },
    ],
  },
  glow: {
    sliderLabel: 'Spread',
    defaultColor: 'match',
    colorOptions: [
      { color: 'match', label: 'Match text' },
      { color: '#ffffff', label: 'White' },
    ],
  },
  outline: {
    sliderLabel: 'Thickness',
    defaultColor: '#ffffff',
    colorOptions: [
      { color: '#000000', label: 'Black' },
      { color: '#ffffff', label: 'White' },
    ],
  },
  'bg-box': {
    sliderLabel: 'Opacity',
    defaultColor: '#000000',
    colorOptions: [
      { color: '#000000', label: 'Black' },
      { color: '#ffffff', label: 'White' },
    ],
  },
} as const;

const alignments = ['left', 'center', 'right'] as const;
const verticalAlignments = ['top', 'middle', 'bottom'] as const;

const VAlignIcon = ({ value }: { value: 'top' | 'middle' | 'bottom' }) => {
  switch (value) {
    case 'top':
      return <ArrowLineUpIcon size={16} />;
    case 'middle':
      return <ArrowsVerticalIcon size={16} />;
    case 'bottom':
      return <ArrowLineDownIcon size={16} />;
  }
};

/** Renders a Popover on desktop, a bottom Drawer on mobile.
 *  `triggerContent` must be a single DOM element (e.g. <button>) — not a
 *  Tooltip wrapper — so Radix Slot can forward onClick correctly. Tooltip is
 *  composed internally with the correct nesting order. */
function ResponsivePopover({
  open,
  onOpenChange,
  triggerContent,
  tooltipLabel,
  tooltipSide = 'bottom',
  title,
  children,
  align = 'start',
  contentClassName,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerContent: React.ReactNode;
  tooltipLabel: string;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  title: string;
  children: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  contentClassName?: string;
}) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DrawerTrigger asChild>{triggerContent}</DrawerTrigger>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide}>{tooltipLabel}</TooltipContent>
        </Tooltip>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-3">{children}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{triggerContent}</PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide}>{tooltipLabel}</TooltipContent>
      </Tooltip>
      <PopoverContent align={align} className={contentClassName ?? 'w-56 p-3 space-y-3'}>
        {children}
      </PopoverContent>
    </Popover>
  );
}

interface TextOverlayToolbarProps {
  stageSize: { width: number; height: number };
}

export function TextOverlayToolbar({ stageSize }: TextOverlayToolbarProps) {
  const { elements, selectedId, updateElement } = useTextEditorStore();
  const selectedElement = elements.find((e) => e.id === selectedId);
  const [showFormatPopover, setShowFormatPopover] = useState(false);
  const [showAlignPopover, setShowAlignPopover] = useState(false);

  const isBold = (selectedElement?.fontWeight ?? 400) >= 700;
  const isItalic = selectedElement?.isItalic ?? false;
  const isUnderline = selectedElement?.isUnderline ?? false;
  const currentColor = resolveFillColor(selectedElement?.fill ?? '#ffffff');

  const toggleBold = () => {
    if (!selectedElement) return;
    const currentWeight = selectedElement.fontWeight ?? 400;
    updateElement(selectedElement.id, {
      fontWeight: currentWeight >= 700 ? 400 : 700,
    });
  };

  const toggleItalic = () => {
    if (!selectedElement) return;
    updateElement(selectedElement.id, {
      isItalic: !selectedElement.isItalic,
    });
  };

  const toggleUnderline = () => {
    if (!selectedElement) return;
    updateElement(selectedElement.id, {
      isUnderline: !selectedElement.isUnderline,
    });
  };

  const setColor = (color: string) => {
    if (!selectedElement) return;
    updateElement(selectedElement.id, { fill: color });
  };

  if (!selectedElement) return null;

  const toggleAllCaps = () =>
    updateElement(selectedElement.id, {
      textTransform: selectedElement.textTransform === 'uppercase' ? 'none' : 'uppercase',
    });

  const AlignIcon =
    selectedElement.textAlign === 'center'
      ? TextAlignCenterIcon
      : selectedElement.textAlign === 'right'
        ? TextAlignRightIcon
        : TextAlignLeftIcon;

  const cycleAlignment = () => {
    const idx = alignments.indexOf(selectedElement.textAlign ?? 'left');
    const next = alignments[(idx + 1) % 3];
    updateElement(selectedElement.id, { textAlign: next });
  };

  const cycleVerticalAlignment = () => {
    if (!selectedElement) return;
    const idx = verticalAlignments.indexOf(selectedElement.verticalAlign ?? 'top');
    const next = verticalAlignments[(idx + 1) % 3];
    // V-Align requires the element to span the full stage height starting at y=0.
    // Without anchoring y=0, the container starts midway down the stage and the
    // text center/bottom ends up outside the visible area.
    updateElement(selectedElement.id, {
      verticalAlign: next,
      ...(next !== 'top' &&
        stageSize.height > 0 && {
          y: 0,
          height: stageSize.height,
        }),
    });
  };

  const centerHorizontally = () => {
    if (!selectedElement) return;
    updateElement(selectedElement.id, {
      x: (stageSize.width - selectedElement.width) / 2,
    });
  };

  const centerVertically = () => {
    if (!selectedElement) return;
    const h =
      selectedElement.height ?? selectedElement.fontSize * (selectedElement.lineHeight ?? 1.2);
    updateElement(selectedElement.id, {
      y: (stageSize.height - h) / 2,
    });
  };

  return (
    <div className="flex flex-col">
      <TooltipProvider delayDuration={300}>
        {/* ── Row 1: Font, Size, Color, Effects ── */}
        <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 h-10">
          {/* 1. Font selector */}
          <DropdownMenu
            onOpenChange={(open) => {
              // Lazy-load every picker font when the menu opens so the previews
              // render in the real typeface (and the chosen one is already warm).
              if (open) OVERLAY_FONT_FAMILIES.forEach((f) => void loadGoogleFont(f));
            }}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="flat"
                    size="sm"
                    aria-label="Font"
                    className="h-7 gap-1 rounded-md px-2 hover:bg-muted shrink-0"
                  >
                    <span className="max-w-[90px] truncate text-foreground/85">
                      {selectedElement.fontFamily}
                    </span>
                    <CaretDownIcon className="size-3 text-muted-foreground" weight="regular" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">Font</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start" className="max-h-[300px] overflow-y-auto">
              {FONTS.map((font) => (
                <DropdownMenuItem
                  key={font}
                  onClick={() => {
                    // Load the font BEFORE applying so Konva rasterizes the real
                    // glyphs instead of the system fallback (the old bug).
                    void loadGoogleFont(font).then(() =>
                      updateElement(selectedElement.id, { fontFamily: font }),
                    );
                  }}
                  style={{ fontFamily: font }}
                >
                  {font}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* 2. Size controls (-, px, +) */}
          <div className="flex items-center shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() =>
                    updateElement(selectedElement.id, {
                      fontSize: Math.max(8, selectedElement.fontSize - 4),
                    })
                  }
                  aria-label="Decrease size"
                  className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <MinusIcon className="size-3" weight="bold" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Decrease size</TooltipContent>
            </Tooltip>
            <span className="w-7 text-center text-caption font-medium tabular-nums text-foreground/85">
              {Math.round(selectedElement.fontSize)}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() =>
                    updateElement(selectedElement.id, {
                      fontSize: Math.min(200, selectedElement.fontSize + 4),
                    })
                  }
                  aria-label="Increase size"
                  className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <PlusIcon className="size-3" weight="bold" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Increase size</TooltipContent>
            </Tooltip>
          </div>

          {/* 3. Color popover */}
          <ResponsivePopover
            triggerContent={
              <button
                type="button"
                aria-label="Text color"
                className="grid size-7 place-items-center rounded-md hover:bg-muted shrink-0"
              >
                <span
                  className="size-4 rounded-full ring-1 ring-border"
                  style={{ backgroundColor: currentColor }}
                />
              </button>
            }
            tooltipLabel="Text color"
            title="Text Color"
            align="start"
            contentClassName="w-auto p-2"
          >
            <div className="grid grid-cols-6 gap-1.5">
              {COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setColor(color)}
                  aria-label={`Set color to ${color}`}
                  className={cn(
                    'size-6 rounded-full ring-1 ring-border transition-transform hover:scale-110',
                    currentColor === color && 'ring-2 ring-foreground',
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
              <div className="relative size-6 rounded-full ring-1 ring-border bg-[conic-gradient(from_0deg,red,yellow,green,blue,purple,red)]">
                <input
                  type="color"
                  value={currentColor}
                  onChange={(e) => setColor(e.target.value)}
                  aria-label="Custom color"
                  className="absolute inset-0 size-full cursor-pointer opacity-0"
                />
              </div>
            </div>
            {/* Gradient toggle */}
            <div className="flex items-center gap-2 border-t border-border pt-2 mt-2">
              <Button
                variant="flat"
                size="sm"
                className={cn(
                  'h-7 text-caption flex-1',
                  typeof selectedElement.fill !== 'string' && 'bg-accent',
                )}
                onClick={() => {
                  if (typeof selectedElement.fill !== 'string') {
                    // Switch back to solid
                    updateElement(selectedElement.id, {
                      fill: selectedElement.fill.colors[0] ?? '#ffffff',
                    });
                  } else {
                    // Switch to gradient
                    updateElement(selectedElement.id, {
                      fill: {
                        type: 'linear',
                        colors: [selectedElement.fill, '#000000'],
                        angle: 180,
                      },
                    });
                  }
                }}
              >
                Gradient
              </Button>
            </div>
            {/* Gradient controls */}
            {typeof selectedElement.fill !== 'string' &&
              (() => {
                const grad = selectedElement.fill;
                return (
                  <div className="space-y-2 border-t border-border pt-2 mt-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={grad.colors[0]}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            fill: { ...grad, colors: [e.target.value, grad.colors[1]] },
                          })
                        }
                        className="size-6 rounded cursor-pointer border border-border"
                      />
                      <div
                        className="h-4 flex-1 rounded border border-border"
                        style={{
                          background: `linear-gradient(${grad.angle}deg, ${grad.colors.join(', ')})`,
                        }}
                      />
                      <input
                        type="color"
                        value={grad.colors[1]}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            fill: { ...grad, colors: [grad.colors[0], e.target.value] },
                          })
                        }
                        className="size-6 rounded cursor-pointer border border-border"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-micro text-muted-foreground w-8">Angle</span>
                      <Slider
                        value={[grad.angle]}
                        min={0}
                        max={360}
                        step={15}
                        onValueChange={([val]) =>
                          updateElement(selectedElement.id, {
                            fill: { ...grad, angle: val },
                          })
                        }
                        className="flex-1"
                      />
                      <span className="text-caption tabular-nums w-8 text-right">
                        {grad.angle}&deg;
                      </span>
                    </div>
                  </div>
                );
              })()}
          </ResponsivePopover>

          {/* 4. Backdrop / Effects popover */}
          <ResponsivePopover
            triggerContent={
              <button
                type="button"
                aria-label="Backdrop"
                className={cn(
                  'grid size-7 place-items-center rounded-md text-foreground/85 hover:bg-muted shrink-0',
                  selectedElement.contrastMode !== 'none' && 'bg-muted',
                )}
              >
                <DropHalfBottomIcon className="size-3.5" weight="regular" />
              </button>
            }
            tooltipLabel="Backdrop effects"
            title="Backdrop"
            align="end"
          >
            {/* Style row */}
            <div className="space-y-1.5">
              <span className="text-caption text-muted-foreground">Style</span>
              <div className="flex items-center gap-1">
                {[
                  {
                    mode: 'none' as const,
                    label: 'None',
                    icon: <XIcon className="size-3" weight="bold" />,
                  },
                  {
                    mode: 'drop-shadow' as const,
                    label: 'Shadow',
                    icon: (
                      <span className="font-serif text-caption font-bold drop-shadow-md">T</span>
                    ),
                  },
                  {
                    mode: 'glow' as const,
                    label: 'Glow',
                    icon: (
                      <span
                        className="font-serif text-caption font-bold"
                        style={{ textShadow: '0 0 4px currentColor' }}
                      >
                        T
                      </span>
                    ),
                  },
                  {
                    mode: 'outline' as const,
                    label: 'Outline',
                    icon: (
                      <span
                        className="font-serif text-caption font-bold"
                        style={{
                          WebkitTextStroke: '1px currentColor',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        T
                      </span>
                    ),
                  },
                  {
                    mode: 'bg-box' as const,
                    label: 'Box',
                    icon: (
                      <span className="font-serif text-micro font-bold bg-foreground text-background px-0.5 rounded-sm">
                        T
                      </span>
                    ),
                  },
                ].map((style) => (
                  <Tooltip key={style.mode}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => {
                          if (style.mode === 'none') {
                            updateElement(selectedElement.id, {
                              contrastMode: 'none',
                              contrastPadding: { top: 0, right: 0, bottom: 0, left: 0 },
                            });
                          } else {
                            const config = CONTRAST_MODE_CONFIG[style.mode];
                            const intensity = selectedElement.contrastIntensity || 90;
                            const defaultColor =
                              config.defaultColor === 'match'
                                ? resolveFillColor(selectedElement.fill)
                                : config.defaultColor;
                            updateElement(selectedElement.id, {
                              contrastMode: style.mode,
                              contrastColor: defaultColor,
                              contrastIntensity: intensity,
                              contrastPadding: computeContrastPadding(style.mode, intensity),
                            });
                          }
                        }}
                        className={cn(
                          'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                          selectedElement.contrastMode === style.mode &&
                            'bg-accent text-foreground',
                        )}
                      >
                        {style.icon}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-caption">
                      {style.label}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </div>

            {/* Color + Intensity — only shown when a contrast mode is active */}
            {selectedElement.contrastMode !== 'none' && (
              <>
                <div className="space-y-1.5">
                  <span className="text-caption text-muted-foreground">Color</span>
                  <div className="flex items-center gap-1.5">
                    {CONTRAST_MODE_CONFIG[
                      selectedElement.contrastMode as keyof typeof CONTRAST_MODE_CONFIG
                    ]?.colorOptions.map((opt) => {
                      const resolvedColor =
                        opt.color === 'match' ? resolveFillColor(selectedElement.fill) : opt.color;
                      const isActive = selectedElement.contrastColor === resolvedColor;
                      return (
                        <Tooltip key={opt.label}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() =>
                                updateElement(selectedElement.id, {
                                  contrastColor: resolvedColor,
                                })
                              }
                              className={cn(
                                'size-5 rounded-full border border-border shadow-sm transition-transform hover:scale-110',
                                isActive && 'ring-2 ring-ring ring-offset-1 ring-offset-background',
                              )}
                              style={{ backgroundColor: resolvedColor }}
                              aria-label={opt.label}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-caption">
                            {opt.label}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                    {/* Custom color swatch */}
                    <div
                      className={cn(
                        'relative size-5 rounded-full border border-border shadow-sm transition-transform hover:scale-110',
                        !['#000000', '#ffffff', resolveFillColor(selectedElement.fill)].includes(
                          selectedElement.contrastColor,
                        ) && 'ring-2 ring-ring ring-offset-1 ring-offset-background',
                      )}
                      style={{
                        background:
                          'conic-gradient(from 0deg, red, yellow, green, blue, purple, red)',
                      }}
                    >
                      <input
                        type="color"
                        value={selectedElement.contrastColor}
                        onChange={(e) =>
                          updateElement(selectedElement.id, { contrastColor: e.target.value })
                        }
                        aria-label="Custom color"
                        className="absolute inset-0 size-full cursor-pointer opacity-0"
                      />
                    </div>
                  </div>
                </div>

                {(() => {
                  const config =
                    CONTRAST_MODE_CONFIG[
                      selectedElement.contrastMode as keyof typeof CONTRAST_MODE_CONFIG
                    ];
                  if (!config) return null;
                  return (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-caption text-muted-foreground">
                          {config.sliderLabel}
                        </span>
                        <span className="text-caption tabular-nums text-muted-foreground">
                          {selectedElement.contrastIntensity}%
                        </span>
                      </div>
                      <Slider
                        value={[selectedElement.contrastIntensity]}
                        min={0}
                        max={100}
                        step={1}
                        onValueChange={([val]) =>
                          updateElement(selectedElement.id, {
                            contrastIntensity: val,
                            contrastPadding: computeContrastPadding(
                              selectedElement.contrastMode,
                              val,
                            ),
                          })
                        }
                      />
                    </div>
                  );
                })()}

                {selectedElement.contrastMode === 'bg-box' && (
                  <div className="flex items-center gap-2 pt-2">
                    <span className="text-detail text-muted-foreground shrink-0">Shape</span>
                    <div className="flex items-center gap-0.5">
                      {(['rectangle', 'circle', 'ellipse', 'star', 'triangle'] as const).map(
                        (shape) => {
                          const icons: Record<string, React.ElementType> = {
                            rectangle: RectangleIcon,
                            circle: CircleIcon,
                            ellipse: CircleIcon,
                            star: StarIcon,
                            triangle: TriangleIcon,
                          };
                          const Icon = icons[shape];
                          return (
                            <button
                              key={shape}
                              type="button"
                              onClick={() =>
                                updateElement(selectedElement.id, { boxShapeType: shape })
                              }
                              className={cn(
                                'grid size-7 place-items-center rounded-md',
                                (selectedElement.boxShapeType ?? 'rectangle') === shape
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-foreground/60 hover:bg-muted',
                              )}
                            >
                              <Icon size={14} />
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>
                )}

                {selectedElement.contrastMode === 'bg-box' && (
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-detail text-muted-foreground shrink-0 w-16">Feather</span>
                    <Slider
                      value={[selectedElement.boxFeather ?? 0]}
                      min={0}
                      max={100}
                      step={1}
                      className="flex-1"
                      onValueChange={([v]) =>
                        updateElement(selectedElement.id, { boxFeather: v }, { skipHistory: true })
                      }
                      onValueCommit={([v]) => updateElement(selectedElement.id, { boxFeather: v })}
                    />
                    <span className="w-8 text-right text-detail tabular-nums text-muted-foreground">
                      {selectedElement.boxFeather ?? 0}%
                    </span>
                  </div>
                )}

                {selectedElement.contrastMode === 'bg-box' &&
                  (selectedElement.boxShapeType ?? 'rectangle') === 'rectangle' && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-caption text-muted-foreground">Roundness</span>
                        <span className="text-caption tabular-nums text-muted-foreground">
                          {selectedElement.boxRoundness ?? 0}%
                        </span>
                      </div>
                      <Slider
                        value={[selectedElement.boxRoundness ?? 0]}
                        min={0}
                        max={100}
                        step={1}
                        onValueChange={([val]) =>
                          updateElement(selectedElement.id, { boxRoundness: val })
                        }
                      />
                    </div>
                  )}
              </>
            )}
          </ResponsivePopover>
        </div>

        {/* ── Row 2: B/I/U, Alignment, Format, Position ── */}
        {selectedElement && (
          <div className="flex items-center gap-1 px-3 sm:px-4 h-10 md:h-8 overflow-x-auto scrollbar-hide">
            {/* B/I/U toggles */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleBold}
                  aria-label="Bold"
                  className={cn(
                    'grid size-10 md:size-8 place-items-center rounded-md text-foreground/85 hover:bg-muted shrink-0',
                    isBold && 'bg-accent text-foreground',
                  )}
                >
                  <TextBolderIcon size={16} weight={isBold ? 'bold' : 'regular'} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Bold</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleItalic}
                  aria-label="Italic"
                  className={cn(
                    'grid size-10 md:size-8 place-items-center rounded-md text-foreground/85 hover:bg-muted shrink-0',
                    isItalic && 'bg-accent text-foreground',
                  )}
                >
                  <TextItalicIcon size={16} weight={isItalic ? 'bold' : 'regular'} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Italic</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleUnderline}
                  aria-label="Underline"
                  className={cn(
                    'grid size-10 md:size-8 place-items-center rounded-md text-foreground/85 hover:bg-muted shrink-0',
                    isUnderline && 'bg-accent text-foreground',
                  )}
                >
                  <TextUnderlineIcon size={16} weight={isUnderline ? 'bold' : 'regular'} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Underline</TooltipContent>
            </Tooltip>

            {/* Separator */}
            <div className="mx-1 h-4 w-px bg-border/40 shrink-0" />

            {/* Align-H cycle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={cycleAlignment}
                  aria-label="Text alignment"
                  className="grid size-10 md:size-8 place-items-center rounded-md text-foreground/85 hover:bg-muted shrink-0"
                >
                  <AlignIcon size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {(selectedElement.textAlign ?? 'left').charAt(0).toUpperCase() +
                  (selectedElement.textAlign ?? 'left').slice(1)}
              </TooltipContent>
            </Tooltip>

            {/* Align-V cycle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={cycleVerticalAlignment}
                  aria-label="Vertical alignment"
                  className="grid size-10 md:size-8 place-items-center rounded-md text-foreground/85 hover:bg-muted shrink-0"
                >
                  <VAlignIcon value={selectedElement.verticalAlign ?? 'top'} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {`V-Align: ${
                  (selectedElement.verticalAlign ?? 'top').charAt(0).toUpperCase() +
                  (selectedElement.verticalAlign ?? 'top').slice(1)
                }`}
              </TooltipContent>
            </Tooltip>

            {/* Separator */}
            <div className="mx-1 h-4 w-px bg-border/40 shrink-0" />

            {/* Aa format popover (slimmed — no B/I/U, no V-Align) */}
            <ResponsivePopover
              open={showFormatPopover}
              onOpenChange={setShowFormatPopover}
              triggerContent={
                <button
                  type="button"
                  aria-label="Format"
                  className={cn(
                    'grid size-10 md:size-8 place-items-center rounded-md text-foreground/85 hover:bg-muted shrink-0',
                    showFormatPopover && 'bg-accent',
                  )}
                >
                  <span className="font-semibold text-sm">Aa</span>
                </button>
              }
              tooltipLabel="Format (caps, weight, spacing)"
              title="Format"
              align="start"
            >
              {/* All Caps */}
              <div className="flex items-center justify-between">
                <span className="text-caption text-muted-foreground">All Caps</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="flat"
                      size="sm"
                      className={cn(
                        'h-7 w-7 p-0',
                        selectedElement.textTransform === 'uppercase' && 'bg-accent',
                      )}
                      onClick={toggleAllCaps}
                    >
                      <span className="font-bold text-detail tracking-tight">AA</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Toggle All Caps</TooltipContent>
                </Tooltip>
              </div>

              {/* Font Weight */}
              <div className="flex items-center justify-between">
                <span className="text-caption text-muted-foreground">Weight</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="flat" size="sm" className="h-7 gap-1 px-2">
                      {FONT_WEIGHTS.find((w) => w.value === (selectedElement.fontWeight ?? 400))
                        ?.label ?? 'Regular'}
                      <CaretDownIcon className="size-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="max-h-48 overflow-y-auto">
                    {FONT_WEIGHTS.map((w) => (
                      <DropdownMenuItem
                        key={w.value}
                        onClick={() => updateElement(selectedElement.id, { fontWeight: w.value })}
                        className={cn(selectedElement.fontWeight === w.value && 'bg-accent')}
                      >
                        <span style={{ fontWeight: w.value }}>{w.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Line Height */}
              <div className="flex items-center justify-between">
                <span className="text-caption text-muted-foreground">Line height</span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="flat"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() =>
                      updateElement(selectedElement.id, {
                        lineHeight: Math.max(0.8, (selectedElement.lineHeight ?? 1.2) - 0.1),
                      })
                    }
                  >
                    <MinusIcon className="size-3" />
                  </Button>
                  <span className="w-8 text-center text-caption tabular-nums">
                    {(selectedElement.lineHeight ?? 1.2).toFixed(1)}
                  </span>
                  <Button
                    variant="flat"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() =>
                      updateElement(selectedElement.id, {
                        lineHeight: Math.min(3.0, (selectedElement.lineHeight ?? 1.2) + 0.1),
                      })
                    }
                  >
                    <PlusIcon className="size-3" />
                  </Button>
                </div>
              </div>

              {/* Letter Spacing */}
              <div className="flex items-center justify-between">
                <span className="text-caption text-muted-foreground">Letter spacing</span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="flat"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() =>
                      updateElement(selectedElement.id, {
                        letterSpacing: Math.max(-0.05, (selectedElement.letterSpacing ?? 0) - 0.01),
                      })
                    }
                  >
                    <MinusIcon className="size-3" />
                  </Button>
                  <span className="w-8 text-center text-caption tabular-nums">
                    {`${Math.round((selectedElement.letterSpacing ?? 0) * 100)}%`}
                  </span>
                  <Button
                    variant="flat"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() =>
                      updateElement(selectedElement.id, {
                        letterSpacing: Math.min(0.2, (selectedElement.letterSpacing ?? 0) + 0.01),
                      })
                    }
                  >
                    <PlusIcon className="size-3" />
                  </Button>
                </div>
              </div>
            </ResponsivePopover>

            {/* Align popover — controlled so it stays open after tapping
                a centering button, letting the user hit both in sequence. */}
            <ResponsivePopover
              open={showAlignPopover}
              onOpenChange={setShowAlignPopover}
              triggerContent={
                <button
                  type="button"
                  aria-label="Align"
                  className="grid size-10 md:size-8 place-items-center rounded-md text-foreground/85 hover:bg-muted shrink-0"
                >
                  <AlignCenterHorizontalSimpleIcon size={16} />
                </button>
              }
              tooltipLabel="Align (center on canvas)"
              title="Align"
              align="end"
              contentClassName="w-auto p-2"
            >
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={centerHorizontally}
                      aria-label="Center horizontally"
                      className="grid size-8 place-items-center rounded-md text-foreground/85 hover:bg-muted"
                    >
                      <AlignCenterHorizontalSimpleIcon size={16} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Center horizontally</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={centerVertically}
                      aria-label="Center vertically"
                      className="grid size-8 place-items-center rounded-md text-foreground/85 hover:bg-muted"
                    >
                      <AlignCenterVerticalSimpleIcon size={16} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Center vertically</TooltipContent>
                </Tooltip>
              </div>
            </ResponsivePopover>

            <div className="ml-auto shrink-0">
              <ZOrderButtons elementId={selectedElement.id} />
            </div>
          </div>
        )}
      </TooltipProvider>
    </div>
  );
}
